import { useEffect, useState } from 'react';
import type * as React from 'react';
import { cn } from '../../lib/cn.ts';

/** How far one arrow key moves the handle. */
const DEFAULT_STEP = 16;

/* Which way the box grows when the pointer moves left. */
type Edge = 'left' | 'right';

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/* Where the drag started: the pointer, and the size it was dragging. */
interface Drag {
  x: number;
  size: number;
}

interface KeyedSize {
  value: number;
  min: number;
  max: number;
  step: number;
  edge: Edge;
}

/*
 * What a key press asks for, or null when the key is not one of ours. Towards
 * the edge the handle sits on is bigger: that is the direction the box grows.
 */
function keyedSize(
  key: string,
  { value, min, max, step, edge }: KeyedSize,
): number | null {
  const towardsTheEdge = edge === 'left' ? 'ArrowLeft' : 'ArrowRight';
  const awayFromIt = edge === 'left' ? 'ArrowRight' : 'ArrowLeft';

  if (key === towardsTheEdge) return value + step;
  if (key === awayFromIt) return value - step;
  if (key === 'Home') return min;
  if (key === 'End') return max;

  return null;
}

export interface ResizeHandleProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'onChange'
> {
  /** Names the control for screen readers — "Resize the panel". */
  label: string;
  /** The size it is reporting now, in pixels. */
  value: number;
  min: number;
  max: number;
  /** How far one arrow key moves it. Defaults to 16px. */
  step?: number;
  /** How far one arrow key moves it with shift held. Defaults to `step`. */
  shiftStep?: number;
  /**
   * The size the box is drawn at before anyone drags it. Given one, Home and a
   * double-click put it back there; without one, Home goes to `min`.
   */
  defaultValue?: number;
  /** Always inside min…max: the handle clamps before it reports. */
  onChange: (value: number) => void;
  /**
   * Told that the size was put back rather than chosen, so an owner that
   * remembers a chosen width can stop remembering one. Without it, going back
   * is reported as any other change.
   */
  onReset?: () => void;
  /**
   * Which edge of its box the handle sits on. On the left edge — a panel docked
   * to the right of the window — dragging away from the box grows it.
   */
  edge?: Edge;
}

/**
 * The grab strip that resizes the box it sits on.
 *
 * A separator, not a button: it stands between two things and says where it
 * stands, which is what lets a screen reader announce a width at all. It is
 * focusable, so it can be moved without a pointer — the arrow keys step it,
 * shift steps it further, End takes it to its widest and Home puts it back.
 * Dragging is the same thing with a mouse.
 *
 * It only reports; it never holds the size. Whoever draws the box owns that
 * number, because they are also the one who has to remember it.
 *
 * Positioned absolutely: the box it resizes must be `relative`.
 */
export function ResizeHandle({
  label,
  value,
  min,
  max,
  step = DEFAULT_STEP,
  shiftStep,
  defaultValue,
  onChange,
  onReset,
  edge = 'left',
  className,
  onPointerDown,
  onKeyDown,
  onDoubleClick,
  ...props
}: ResizeHandleProps) {
  const [drag, setDrag] = useState<Drag | null>(null);

  useEffect(() => {
    if (!drag) return;

    /*
     * On the window rather than on the handle: a pointer moving faster than the
     * panel redraws leaves the strip behind, and a drag that stops the moment
     * the cursor outruns it is a drag that keeps slipping out of the user's
     * hand.
     */
    function resizeTo(event: PointerEvent) {
      if (!drag) return;
      const moved = edge === 'left' ? drag.x - event.clientX : event.clientX - drag.x;

      onChange(clamp(drag.size + moved, min, max));
    }

    function stop() {
      setDrag(null);
    }

    /* Or the page selects text under the cursor for the length of the drag. */
    const selection = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', resizeTo);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);

    return () => {
      document.body.style.userSelect = selection;
      window.removeEventListener('pointermove', resizeTo);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [drag, edge, min, max, onChange]);

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    onPointerDown?.(event);
    if (event.button !== 0) return;

    /* Or the pointer that grabbed the strip also lands on whatever is under it. */
    event.preventDefault();
    setDrag({ x: event.clientX, size: value });
  }

  /* Home and a double-click are the same gesture: put it back where it began. */
  function reset() {
    if (onReset) onReset();
    else if (defaultValue !== undefined) onChange(clamp(defaultValue, min, max));
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);

    if (event.key === 'Home' && defaultValue !== undefined) {
      event.preventDefault();
      reset();
      return;
    }

    const next = keyedSize(event.key, {
      value,
      min,
      max,
      step: event.shiftKey ? (shiftStep ?? step) : step,
      edge,
    });
    if (next === null) return;

    event.preventDefault();
    onChange(clamp(next, min, max));
  }

  /* The pointer's Home: back to the size the box was designed at. */
  function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>) {
    onDoubleClick?.(event);
    if (defaultValue === undefined) return;

    reset();
  }

  return (
    <div
      data-slot='resize-handle'
      data-dragging={drag !== null}
      role='separator'
      aria-label={label}
      aria-orientation='vertical'
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={startDrag}
      onKeyDown={handleKeyDown}
      onDoubleClick={handleDoubleClick}
      className={cn(
        'absolute inset-y-0 z-10 w-3 cursor-col-resize touch-none',
        edge === 'left' ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        /* The line only shows when it is being reached for, or held. */
        'after:absolute after:inset-y-0 after:left-1/2 after:w-0.5 after:-translate-x-1/2 after:transition-colors',
        'hover:after:bg-accent focus-visible:after:bg-accent data-[dragging=true]:after:bg-accent',
        className,
      )}
      {...props}
    />
  );
}
